import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useProjects } from '../contexts/ProjectsContext';

const REALTIME_REFETCH_DEBOUNCE_MS = 200;
const FALLBACK_POLL_INTERVAL_MS = 5000;

export function useSupabaseProjects() {
  const { setProjects } = useProjects();
  const latestFetchIdRef = useRef(0);
  const realtimeRefetchTimerRef = useRef<number | null>(null);

  // Fetch estimations from Supabase
  const fetchProjects = useCallback(async () => {
    if (!supabase) return;
    const fetchId = ++latestFetchIdRef.current;

    // Get the logged-in username from localStorage
    const username = localStorage.getItem('bidflow_username');
    if (!username) return; // Don't fetch if not logged in

    // Get the current user's ID
    const { data: { user } } = await supabase.auth.getUser();
    const SUPER_ADMIN_ID = '32e2a58f-b967-4315-9819-03c4937b8385';

    // Build query - founders and super admin can see everything, others only see their own
    let query = supabase
      .from('estimations')
      .select(`
        *,
        clients (
          name
        ),
        estimation_files (
          id,
          file_name,
          file_size,
          file_type,
          storage_path,
          file_order
        ),
        estimation_specs (
          id,
          file_name,
          file_size,
          file_type,
          storage_path,
          file_order
        )
      `);

    // Only filter by username if not founders account or super admin
    const isSuperAdmin = user?.id === SUPER_ADMIN_ID;
    if (username !== 'founders' && !isSuperAdmin) {
      query = query.eq('username', username);
    } else if (isSuperAdmin) {
      console.log('🔓 Super Admin Access: Showing all projects across the application');
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      return;
    }

    if (fetchId !== latestFetchIdRef.current) {
      return;
    }

    if (data) {
      const hasLightingFixtureComponentsInKey = (projectKey: any): boolean => {
        const components = Array.isArray(projectKey?.components) ? projectKey.components : [];
        return components.some((component: any) => {
          const componentType = String(component?.componentType || component?.symbolType || '').toLowerCase();
          if (!componentType.includes('lighting')) return false;
          const symbol = String(component?.symbol || '').trim();
          if (symbol.length === 0) return false;
          return symbol !== '__LIGHTING_GROUP__';
        });
      };

      const formattedProjects = await Promise.all(data.map(async (p: any) => {
        // Get files from estimation_files table (new multi-file support)
        let files: Array<{
          id: string;
          name: string;
          size: number;
          type: string;
          storagePath: string;
          url: string;
        }> = [];

        if (p.estimation_files && p.estimation_files.length > 0) {
          files = p.estimation_files
            .sort((a: any, b: any) => a.file_order - b.file_order)
            .map((f: any) => {
              const { data: urlData } = supabase.storage
                .from('bucket')
                .getPublicUrl(f.storage_path);

              return {
                id: f.id,
                name: f.file_name,
                size: f.file_size || 0,
                type: f.file_type || 'application/pdf',
                storagePath: f.storage_path,
                url: urlData.publicUrl,
              };
            });
        }
        // Fallback to legacy blueprint_url field
        else if (p.blueprint_url) {
          files = [{
            id: p.id + '-file',
            name: p.blueprint_name || 'Blueprint',
            size: p.blueprint_size || 0,
            type: 'application/pdf',
            storagePath: p.blueprint_path || '',
            url: p.blueprint_url,
          }];
        }

        // Get specs from estimation_specs table
        let specs: Array<{
          id: string;
          name: string;
          size: number;
          type: string;
          storagePath: string;
          url: string;
        }> = [];

        if (p.estimation_specs && p.estimation_specs.length > 0) {
          specs = p.estimation_specs
            .sort((a: any, b: any) => a.file_order - b.file_order)
            .map((s: any) => {
              const { data: urlData } = supabase.storage
                .from('bucket')
                .getPublicUrl(s.storage_path);

              return {
                id: s.id,
                name: s.file_name,
                size: s.file_size || 0,
                type: s.file_type || 'application/pdf',
                storagePath: s.storage_path,
                url: urlData.publicUrl,
              };
            });
        }

        return {
          id: p.id,
          name: p.name,
          folderId: p.folder_id || '00000000-0000-0000-0000-000000000001',
          clientId: p.client_id || '',
          clientName: (p.clients && Array.isArray(p.clients) && p.clients.length > 0 ? p.clients[0].name : p.clients?.name) || p.client_name || '',
          description: p.description || '',
          bidDeadline: p.bid_deadline || '',
          status: p.status || 'estimating',
          projectType: p.project_type || 'commercial',
          estimatedBudget: p.estimated_budget ? parseFloat(p.estimated_budget) : 0,
          contractSize: p.contract_size ? parseFloat(p.contract_size) : undefined,
          address: '',
          city: '',
          state: '',
          zipCode: '',
          files: files.length > 0 ? files : undefined,
          specs: specs.length > 0 ? specs : undefined,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          lastOpenedAt: p.last_opened_at || undefined,
          runState: p.run_state || 'idle',
          runStage: p.run_stage || 'none',
          timeLeft: Number.isFinite(p.time_left) ? Number(p.time_left) : 0,
          activeAiJobId: p.active_ai_job_id || null,
          runUpdatedAt: p.run_updated_at || p.updated_at,
          runError: p.run_error || null,
          takeoffScope: p.takeoff_scope || 'full',
          setupGateStatus: p.setup_gate_status || 'ready',
          hasLightingFixtureComponents: hasLightingFixtureComponentsInKey(p.project_key),
          fromSupabase: true,
        };
      }));

      if (fetchId !== latestFetchIdRef.current) {
        return;
      }
      setProjects(formattedProjects);
    }
  }, [setProjects]);

  const scheduleRealtimeFetch = useCallback(() => {
    if (realtimeRefetchTimerRef.current != null) {
      window.clearTimeout(realtimeRefetchTimerRef.current);
    }
    realtimeRefetchTimerRef.current = window.setTimeout(() => {
      realtimeRefetchTimerRef.current = null;
      void fetchProjects();
    }, REALTIME_REFETCH_DEBOUNCE_MS);
  }, [fetchProjects]);

  const applyRealtimeEstimationPatch = useCallback((payload: any) => {
    const eventType = String(payload?.eventType || '').toUpperCase();
    if (eventType === 'DELETE') {
      const deletedId = String(payload?.old?.id || '').trim();
      if (!deletedId) {
        scheduleRealtimeFetch();
        return;
      }
      setProjects((prev) => prev.filter((project) => project.id !== deletedId));
      scheduleRealtimeFetch();
      return;
    }

    const row = payload?.new;
    const rowId = String(row?.id || '').trim();
    if (!rowId) {
      scheduleRealtimeFetch();
      return;
    }

    let patchedProject = false;
    setProjects((prev) =>
      prev.map((project) => {
        if (project.id !== rowId) return project;
        patchedProject = true;
        const numericTimeLeft = Number(row?.time_left);
        return {
          ...project,
          name: row?.name ?? project.name,
          status: row?.status ?? project.status,
          folderId: row?.folder_id || project.folderId,
          clientId: row?.client_id || project.clientId,
          clientName: row?.client_name || project.clientName,
          projectType: row?.project_type || project.projectType,
          updatedAt: row?.updated_at || project.updatedAt,
          lastOpenedAt: row?.last_opened_at || project.lastOpenedAt,
          runState: row?.run_state || project.runState,
          runStage: row?.run_stage || project.runStage,
          timeLeft: Number.isFinite(numericTimeLeft)
            ? Math.max(0, Math.floor(numericTimeLeft))
            : project.timeLeft,
          activeAiJobId:
            row?.active_ai_job_id !== undefined
              ? row.active_ai_job_id || null
              : project.activeAiJobId,
          runUpdatedAt: row?.run_updated_at || row?.updated_at || project.runUpdatedAt,
          runError:
            row?.run_error !== undefined
              ? row.run_error || null
              : project.runError,
          takeoffScope: row?.takeoff_scope || project.takeoffScope,
          setupGateStatus: row?.setup_gate_status || project.setupGateStatus,
        };
      })
    );

    if (!patchedProject) {
      // If this was an insert or a project outside the current in-memory list, fetch full list.
      scheduleRealtimeFetch();
      return;
    }

    // Follow with a debounced full fetch to keep joined file/spec/client data in sync.
    scheduleRealtimeFetch();
  }, [scheduleRealtimeFetch, setProjects]);

  useEffect(() => {
    if (!supabase) return;

    void fetchProjects();

    // Subscribe to changes on estimations, estimation_files, and estimation_specs
    const subscription = supabase
      .channel('project-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimations' }, (payload: any) => {
        console.log('Estimations changed:', payload);
        applyRealtimeEstimationPatch(payload);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimation_files' }, (payload: any) => {
        console.log('Estimation files changed:', payload);
        scheduleRealtimeFetch();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'estimation_specs' }, (payload: any) => {
        console.log('Estimation specs changed:', payload);
        scheduleRealtimeFetch();
      })
      .subscribe((status: any, err?: any) => {
        if (err) {
          console.error('Subscription error:', err);
        }
        console.log('Subscription status:', status);
      });

    const fallbackPoll = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void fetchProjects();
    }, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      if (realtimeRefetchTimerRef.current != null) {
        window.clearTimeout(realtimeRefetchTimerRef.current);
        realtimeRefetchTimerRef.current = null;
      }
      window.clearInterval(fallbackPoll);
      subscription.unsubscribe();
    };
  }, [applyRealtimeEstimationPatch, fetchProjects, scheduleRealtimeFetch]);

  return { refetch: fetchProjects };
}

